import { useEffect, useRef, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { cellWidth } from "../../../core/methods/tableCellWidth";
import Datatable from "../../../core/common/dataTable";
import { PaginationType } from "../../../types/common-type";
import TimelineModal from "../../../core/common/TimelineModal";
import {
  dummyData_inProgress,
  timelineData_invite,
} from "../../../core/data/js/dummy-data";
import ModifyInviteModal from "../../../core/common/ModifyInviteModal";
import {
  parseDate,
  parseDaysString,
  parseNumber,
  safeLocaleCompare,
} from "../../../core/methods/sorterMethods";
import { formattedDate } from "../../../core/methods/formattedDate";
import {
  SearchFilterErrors,
  SearchFilterRef,
  SearchPayload,
} from "../../../core/common/ManagerSingleSearchFilter/types";
import SearchFilter from "../../../core/common/ManagerSingleSearchFilter/SearchFilter";
import { DEFAULT_STATUS_STYLE, STATUS_STYLES } from "./ApplicantCaseTracking";
import webservice_url from "../../../core/webservice/Webservice_url";
import useApi from "../../../core/customhook/useApi";
import { getSearchableHeader } from "../../../core/utils/getSearchableHeader";
import SnipperLoader from "../../../core/common/loader/SnipperLoader";
import { exportAntdTableToExcel } from "../../../core/utils/exportExcel";

const initialSearchData: SearchPayload = {
  org_ids: [],
  project_ids: [],
  location_name: [],
  program_ids: [],
  department_ids: [],
};

type Props = {
  status_id?: string;
};
const ApplicantInProgress: React.FC<Props> = ({ status_id }) => {
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [showModifyInvite, setShowModifyInvite] = useState<boolean>(false);
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);
  const [itemList, setItemList] = useState(dummyData_inProgress);
  const [errors, setErrors] = useState<SearchFilterErrors>({});

  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});
  const filterRef = useRef<SearchFilterRef>(null);
  const [searchData, setSearchData] =
    useState<SearchPayload>(initialSearchData);

  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000, // limit of records
    total: 0,
  });
  type InviteItem = any;
  const filteredData = (Array.isArray(itemList) ? itemList : []).filter(
    (item: InviteItem) => {
      return Object.entries(inventoryColumnSearch).every(([key, value]) => {
        if (!value) return true;

        return String(item[key as keyof InviteItem] ?? "")
          .toLowerCase()
          .includes(value.toLowerCase());
      });
    },
  );

  // API
  const caseTrackProgressAPI = useApi<any>(
    webservice_url.manager_case_tracking_data,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleViewLog = async (caseId?: string) => {
    if (!caseId) return;
    setShowTimeline(true);
    setTimelineLoading(true);
    try {
      const res = await getCaseLog.callApi({ case_id: caseId });
      setTimelineData(res?.data ?? []);
    } catch (err) {
      console.error(err);
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const fetchCases = async (
    page?: number,
    limit?: number,
    overrideData?: Partial<SearchPayload>,
  ) => {
    try {
      const payload = {
        status: status_id ?? "",
        // ...searchData,
        ...(overrideData ?? searchData),
        page: page ?? pagination?.current ?? 1,
        limit: limit ?? pagination?.pageSize ?? 10,
      };
      const res = await caseTrackProgressAPI.callApi(payload);

      if (!res?.success) {
        setItemList([]);
        return;
      }

      setItemList(res.data?.table ?? []);

      setPagination((prev) => ({
        current: page ?? prev.current,
        pageSize: limit ?? prev.pageSize,
        total: res?.data?.pagination?.total || 0,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const validateFilters = (): boolean => {
    if (searchData.org_ids.length === 0) {
      setErrors({ org_ids: "Organization is required" });
      filterRef.current?.focusField("org_id");
      return false;
    }

    if (searchData.project_ids.length === 0) {
      setErrors({ project_ids: "Account is required" });
      filterRef.current?.focusField("project_id");
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSearch = () => {
    if (!validateFilters()) return;
    fetchCases();
  };

  useEffect(() => {
    if (!status_id) return;
    fetchCases(pagination.current, pagination.pageSize);
    // handleSearch();
  }, []);
  // }, [pagination?.current, pagination?.pageSize]);

  const handleFilterChange = (data: SearchPayload) => {
    const orgChanged = data.org_ids.join(",") !== searchData.org_ids.join(",");

    setSearchData(data);
    setErrors((prev) => ({
      org_ids: data.org_ids.length > 0 ? undefined : prev.org_ids,
      project_ids:
        data.project_ids.length > 0 || orgChanged
          ? undefined
          : prev.project_ids,
    }));
  };

  const handleClear = () => {
    filterRef.current?.clear();
    setErrors({});
  };

  const handleTableChange = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination.current ||
      pgConfig.pageSize !== pagination.pageSize
    ) {
      fetchCases(pgConfig.current, pgConfig.pageSize);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      onCell: () => cellWidth(100),
      align: "center",
    },
    {
      title: getSearchableHeader(
        "Applicant Name",
        "candidate_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "candidate_name",
      onCell: () => cellWidth(200),
      render: (text: any) => <div>{text || "-"}</div>,
      sorter: (a, b) => safeLocaleCompare(a, b, "candidate_name"),
    },
    {
      title: getSearchableHeader(
        "Case ID",
        "case_code",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_code",
      onCell: () => cellWidth(200),
      render: (text: any) => <div>{text || "-"}</div>,
      sorter: (a, b) => safeLocaleCompare(a, b, "case_code"),
    },
    {
      title: getSearchableHeader(
        "Case Spoc",
        "case_spoc_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_spoc_name",
      onCell: () => cellWidth(200),
      render: (text: any) => <div>{text || "-"}</div>,
      sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc_name"),
    },
    {
      title: getSearchableHeader(
        "Case Status",
        "overall_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "overall_status",
      key: "overall_status",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        (a.overall_status ?? "").localeCompare(b.overall_status ?? ""),
      render: (text: any, record: any) => {
        return (
          <span
            style={{
              backgroundColor: record?.overall_color_value || "#6c757d",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "4px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {text ?? "-"}
          </span>
        );
      },
    },
    {
      title: getSearchableHeader(
        "Project",
        "project_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "project_name",
      key: "project_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Program",
        "program_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "program_name",
      key: "program_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "department_name",
      key: "department_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Candidate Id",
        "hrms_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "hrms_id",
      onCell: () => cellWidth(200),
      render: (text: string) => text || "-",
      sorter: (a, b) => safeLocaleCompare(a, b, "hrms_id"),
    },
    {
      title: getSearchableHeader("Email ID", "email", setInventoryColumnSearch),
      dataIndex: "email",
      render: (text: string) => text || "-",
      sorter: (a, b) => safeLocaleCompare(a, b, "email"),
    },
    {
      title: getSearchableHeader(
        "Days Elapsed",
        "days_elapsed",
        setInventoryColumnSearch,
      ),
      dataIndex: "days_elapsed",
      align: "center",
      onCell: () => cellWidth(200),
      render: (text: string) => (text ? `${text} Days` : "-"),
      sorter: (a: any, b: any) =>
        parseDaysString(a.days_elapsed) - parseDaysString(b.days_elapsed),
    },
    {
      title: getSearchableHeader(
        "Phone No.",
        "phone",
        setInventoryColumnSearch,
      ),
      dataIndex: "phone",
      onCell: () => cellWidth(200),
      render: (text: any) => <div>{text || "-"}</div>,
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "phone"),
    },
    {
      title: getSearchableHeader(
        "Invitation Date",
        "invitation_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "invitation_date",
      onCell: () => cellWidth(200),
      render: (text: any) => <div>{formattedDate(text) || "-"}</div>,
      sorter: (a: any, b: any) =>
        parseDate(a.invitation_date).getTime() -
        parseDate(b.invitation_date).getTime(),
    },
    // {
    //   title: getSearchableHeader(
    //     "Expire In",
    //     "inviteExpiryDate",
    //     setInventoryColumnSearch,
    //   ),
    //   dataIndex: "inviteExpiryDate",
    //   onCell: () => cellWidth(200),
    //   render: (text: string) => formattedDate(text) ?? "-",
    //   sorter: (a: any, b: any) =>
    //     parseDate(a.inviteExpiryDate).getTime() -
    //     parseDate(b.inviteExpiryDate).getTime(),
    // },
    {
      title: "Status",
      dataIndex: "candidate_status",
      align: "center",
      onCell: () => cellWidth(200),
      render: (status: string) => {
        const { bg, color } =
          STATUS_STYLES[status?.toLowerCase()] || DEFAULT_STATUS_STYLE;
        return (
          <span
            className="badge rounded-pill d-inline-flex align-items-center gap-1 px-3 py-2 fw-medium"
            style={{ background: bg, color }}
          >
            <span
              className="rounded-circle"
              style={{ width: 6, height: 6, background: color }}
            />
            {status}
          </span>
        );
      },
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "candidate_status"),
    },

    {
      title: "View Log",
      key: "viewLog",
      onCell: () => cellWidth(110),
      render: (_: any, record: any) => (
        <div
          className="text-center text-dark"
          style={{ cursor: "pointer" }}
          onClick={() => handleViewLog(record.case_id)}
        >
          <i className="ti ti-eye"></i>
        </div>
      ),
    },
  ];

  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "In_Progress_Report",
      sheetName: "Application In Progress",
    });
  };

  const loading = caseTrackProgressAPI?.loading;
  return (
    <div className="px-2">
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <SearchFilter
            ref={filterRef}
            value={searchData}
            onChange={handleFilterChange}
            errors={errors}
            storageKey="applicantInProgress"
          />

          {/* Buttons */}

          <div className="d-flex justify-content-end gap-2 mt-2">
            <button
              className="btn btn-primary px-4"
              onClick={handleSearch}
              disabled={caseTrackProgressAPI.loading}
            >
              <i className="ti ti-search me-1"></i>
              Search
            </button>

            <button
              className="btn btn-outline-secondary px-4"
              onClick={handleClear}
            >
              <i className="ti ti-refresh me-1"></i>
              Clear Filter
            </button>
          </div>
        </div>
      </div>

      {/* Table  */}
      <div className="card mt-3">
        {loading ? (
          <SnipperLoader />
        ) : (
          <>
            <div className="d-flex justify-content-end pe-2 py-2 pb-0">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleDownloadExcel}
                disabled={loading}
              >
                <i className="ti ti-download" /> Export to Excel
              </button>
            </div>
            <Datatable
              dataSource={filteredData}
              columns={columns}
              fixedHeader
              tableHeight={650}
              Selection={false}
              pagination={pagination}
              handleTableChange={handleTableChange}
            />
          </>
        )}
      </div>

      {/* Timeline */}
      {showTimeline && (
        <TimelineModal
          timelineData={timelineData}
          loading={timelineLoading}
          onClose={() => {
            setShowTimeline(false);
            setTimelineData([]);
          }}
        />
      )}

      {/* Modify Invite Modal */}
      {showModifyInvite && (
        <ModifyInviteModal
          handleClose={() => setShowModifyInvite(false)}
          handleSubmit={(d) => {
            console.log(d);
            setShowModifyInvite(false);
          }}
        />
      )}
    </div>
  );
};

export default ApplicantInProgress;
